import React, { useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CollectionPreferences,
	CollectionPreferencesProps,
	Container,
	ContentLayout,
	ExpandableSection,
	Grid,
	Header,
	Link,
	Pagination,
	SpaceBetween,
	Table,
	TextFilter,
} from '@amzn/awsui-components-console';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { TaskDefinition } from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';
import CopyableText from 'src/components/findEntity/CopyableText';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { useJwtToken } from 'src/hooks/useJwtToken';
import { SNOW_AUTOPOPULATION_SERVICE_ENDPOINT, SNOW_AUTOPOPULATION_SERVICE_REGION } from 'src/config';
import { getAttributeScopeLabel, isWorkflowScopedAttribute } from 'src/utils/attributeTypeUtils';

interface WorkflowAttributesTableProps {
	taskDefinitions: TaskDefinition[];
	workflowId: string;
}

const ATTR_CONTENT_DISPLAY_OPTIONS = [
	{ id: 'attributeId', label: 'Attribute ID', alwaysVisible: true },
	{ id: 'attributeName', label: 'Attribute Name' },
	{ id: 'taskName', label: 'Task' },
	{ id: 'dataType', label: 'Data Type' },
	{ id: 'mandatory', label: 'Mandatory' },
	{ id: 'attributeType', label: 'Scope' },
	{ id: 'value', label: 'Default Value' },
	{ id: 'description', label: 'Description' },
	{ id: 'regex', label: 'Regex' },
	{ id: 'options', label: 'Options' },
	{ id: 'autoPopulation', label: 'Auto Population' },
];

const ATTR_DEFAULT_VISIBLE = ['attributeId', 'attributeName', 'taskName', 'dataType', 'mandatory', 'autoPopulation'];

type DetailPanelType = 'autoPopulation' | null;

export default function WorkflowAttributesTable({
	taskDefinitions,
	workflowId,
}: WorkflowAttributesTableProps) {
	const { jwtToken, error: jwtTokenError } = useJwtToken();

	const { data: autoPopResponse } = useFetchForSDS<{ definition: Record<string, any> }>({
		path: workflowId && jwtToken ? `/autoPopulation/definitions/${workflowId}` : '',
		method: 'GET',
		url: SNOW_AUTOPOPULATION_SERVICE_ENDPOINT,
		region: SNOW_AUTOPOPULATION_SERVICE_REGION,
		headers: jwtToken ? { 'x-jwt-token': jwtToken } : undefined,
	});

	const autoPopulationMapping = autoPopResponse?.definition || {};

	const [detailPanelType, setDetailPanelType] = useState<DetailPanelType>(null);
	const [selectedAutoPopDef, setSelectedAutoPopDef] = useState<any>(null);
	const [selectedAutoPopAttrId, setSelectedAutoPopAttrId] = useState('');

	const allAttributes = useMemo(() => {
		const attrToTasks = new Map<string, Set<string>>();
		taskDefinitions.forEach((task) => {
			(task.taskInputs || []).forEach((attr: any) => {
				if (isWorkflowScopedAttribute(attr)) {
					if (!attrToTasks.has(attr.attributeId)) {
						attrToTasks.set(attr.attributeId, new Set());
					}
					attrToTasks.get(attr.attributeId)!.add(task.taskName);
				}
			});
		});

		const seen = new Set<string>();
		return taskDefinitions.flatMap((task) => {
			return (task.taskInputs || []).map((attr: any) => {
				const isWorkflowLevel = isWorkflowScopedAttribute(attr);
				const uniqueKey = isWorkflowLevel ? attr.attributeId : `${task.taskName}.${attr.attributeId}`;
				if (seen.has(uniqueKey)) return null;
				seen.add(uniqueKey);

				const taskNames = isWorkflowLevel
					? Array.from(attrToTasks.get(attr.attributeId) || [])
					: [task.taskName];

				return {
					...attr,
					taskName: taskNames.join(', '),
				};
			}).filter(Boolean);
		});
	}, [taskDefinitions]);

	const [attrPreferences, setAttrPreferences] = useState<CollectionPreferencesProps.Preferences>({
		pageSize: 5,
		contentDisplay: ATTR_CONTENT_DISPLAY_OPTIONS.map(({ id }) => ({
			id,
			visible: ATTR_DEFAULT_VISIBLE.includes(id),
		})),
		wrapLines: true,
		stripedRows: getDefaultStripedRows(),
	});

	const {
		items: attrItems,
		filteredItemsCount: attrFilteredCount,
		collectionProps: attrCollectionProps,
		filterProps: attrFilterProps,
		paginationProps: attrPaginationProps,
	} = useCollection(allAttributes, {
		filtering: {},
		pagination: { pageSize: attrPreferences.pageSize },
		sorting: {},
	});

	const ATTR_COLUMNS = useMemo(() => [
		{
			id: 'attributeId',
			header: 'Attribute ID',
			cell: (item: any) => item.attributeId || '-',
			sortingField: 'attributeId',
		},
		{
			id: 'attributeName',
			header: 'Attribute Name',
			cell: (item: any) => item.attributeName || '-',
			sortingField: 'attributeName',
		},
		{
			id: 'taskName',
			header: 'Task',
			cell: (item: any) => {
				const tasks = (item.taskName || '').split(', ').filter(Boolean);
				if (tasks.length === 0) return '-';
				if (tasks.length <= 2) {
					return (
						<SpaceBetween size="xxs">
							{tasks.map((t: string, i: number) => <Box key={i}>{t}</Box>)}
						</SpaceBetween>
					);
				}
				return (
					<SpaceBetween size="xxs">
						{tasks.slice(0, 2).map((t: string, i: number) => <Box key={i}>{t}</Box>)}
						<ExpandableSection headerText={`+${tasks.length - 2} more`} variant="footer">
							<SpaceBetween size="xxs">
								{tasks.slice(2).map((t: string, i: number) => <Box key={i}>{t}</Box>)}
							</SpaceBetween>
						</ExpandableSection>
					</SpaceBetween>
				);
			},
			sortingField: 'taskName',
		},
		{
			id: 'dataType',
			header: 'Data Type',
			cell: (item: any) => item.dataType || '-',
			sortingField: 'dataType',
		},
		{
			id: 'mandatory',
			header: 'Mandatory',
			cell: (item: any) => item.mandatory ? 'Yes' : 'No',
			sortingField: 'mandatory',
		},
		{
			id: 'attributeType',
			header: 'Scope',
			cell: (item: any) => getAttributeScopeLabel(item),
			sortingField: 'attributeType',
		},
		{
			id: 'value',
			header: 'Default Value',
			cell: (item: any) => item.value || '-',
		},
		{
			id: 'description',
			header: 'Description',
			cell: (item: any) => item.description || '-',
		},
		{
			id: 'regex',
			header: 'Regex',
			cell: (item: any) => item.regex || '-',
		},
		{
			id: 'options',
			header: 'Options',
			cell: (item: any) => item.options?.length > 0 ? item.options.join(', ') : '-',
		},
		{
			id: 'autoPopulation',
			header: 'Auto Population',
			cell: (item: any) => {
				const def = autoPopulationMapping[item.attributeId];
				if (!def) return '-';
				return (
					<Link
						variant="primary"
						onFollow={() => {
							setSelectedAutoPopDef(def);
							setSelectedAutoPopAttrId(item.attributeId);
							setDetailPanelType('autoPopulation');
						}}
					>
						{def.autoPopulationType || 'Configured'}
					</Link>
				);
			},
		},
	], [autoPopulationMapping]);

	return (
		<Container>
			{workflowId && jwtTokenError && (
				<Alert type="warning" header="Auto-population details couldn't be loaded">
					{jwtTokenError}
				</Alert>
			)}
			<Grid gridDefinition={[
				{ colspan: detailPanelType ? 8 : 12 },
				{ colspan: detailPanelType ? 4 : 0 },
			]}>
			<Table
				{...attrCollectionProps}
				columnDefinitions={ATTR_COLUMNS}
				items={attrItems}
				variant="embedded"
				stickyHeader
				wrapLines={attrPreferences.wrapLines}
				stripedRows={attrPreferences.stripedRows}
				columnDisplay={attrPreferences.contentDisplay}
				header={
					<Header
						variant="h2"
						counter={`(${attrFilteredCount})`}
						actions={
							<SpaceBetween direction="horizontal" size="xs">
								<TextFilter
									{...attrFilterProps}
									filteringPlaceholder="Search attributes..."
									countText={`${attrFilteredCount} ${attrFilteredCount === 1 ? 'match' : 'matches'}`}
								/>
							</SpaceBetween>
						}
					>
						Workflow Attributes
					</Header>
				}
				empty={
					<Box textAlign="center" color="inherit">
						<b>No attributes</b>
						<Box padding={{ bottom: 's' }} variant="p" color="inherit">
							No attributes defined for this workflow.
						</Box>
					</Box>
				}
				pagination={<Pagination {...attrPaginationProps} />}
				preferences={
					<CollectionPreferences
						title="Preferences"
						confirmLabel="Confirm"
						cancelLabel="Cancel"
						pageSizePreference={{
							title: 'Page size',
							options: [
								{ value: 5, label: '5 attributes' },
								{ value: 10, label: '10 attributes' },
								{ value: 20, label: '20 attributes' },
								{ value: 50, label: '50 attributes' },
							],
						}}
						wrapLinesPreference={{
							label: 'Wrap lines',
							description: 'Select to see all text and wrap lines',
						}}
						stripedRowsPreference={{
							label: 'Striped rows',
							description: 'Select to add alternating shaded rows',
						}}
						contentDisplayPreference={{
							title: 'Column preferences',
							options: ATTR_CONTENT_DISPLAY_OPTIONS,
						}}
						preferences={attrPreferences}
						onConfirm={({ detail }) => setAttrPreferences(detail)}
					/>
				}
			/>

			{detailPanelType && (
				<ContentLayout>
					<Container
						header={
							<Header
								actions={
									<Button
										iconName="close"
										variant="icon"
										onClick={() => setDetailPanelType(null)}
									/>
								}
								variant="h3"
							>
								Auto Population
							</Header>
						}
					>
						{detailPanelType === 'autoPopulation' && selectedAutoPopDef && (
							<SpaceBetween size="s">
								<Box>
									<Box variant="awsui-key-label">Attribute</Box>
									<Box>{selectedAutoPopAttrId}</Box>
								</Box>
								<Box>
									<Box variant="awsui-key-label">Type</Box>
									<Box>{selectedAutoPopDef.autoPopulationType || '-'}</Box>
								</Box>
								<Box>
									<Box variant="awsui-key-label">Auto Population ID</Box>
									<Box>{selectedAutoPopDef.autoPopulationId || '-'}</Box>
								</Box>
								<Box>
									<Box variant="awsui-key-label">Description</Box>
									<Box>{selectedAutoPopDef.description || '-'}</Box>
								</Box>
								<Box>
									<Box variant="awsui-key-label">Overridable</Box>
									<Box>{selectedAutoPopDef.isOverridable ? 'Yes' : 'No'}</Box>
								</Box>
								{selectedAutoPopDef.operationId && (
									<Box>
										<Box variant="awsui-key-label">Operation ID</Box>
										<CopyableText textToCopy={selectedAutoPopDef.operationId} label="Operation ID">
											{selectedAutoPopDef.operationId}
										</CopyableText>
									</Box>
								)}
								<Box>
									<Box variant="awsui-key-label">Dependencies</Box>
									<Box>{selectedAutoPopDef.dependencies?.length > 0 ? selectedAutoPopDef.dependencies.join(', ') : '-'}</Box>
								</Box>
								<Box>
									<Box variant="awsui-key-label">Dependents</Box>
									<Box>{selectedAutoPopDef.dependents?.length > 0 ? selectedAutoPopDef.dependents.join(', ') : '-'}</Box>
								</Box>
								{selectedAutoPopDef.requestMapping && Object.keys(selectedAutoPopDef.requestMapping).length > 0 && (
									<Box>
										<Box variant="awsui-key-label">Request Mapping</Box>
										<Box>
											{Object.entries(selectedAutoPopDef.requestMapping).map(([key, value]) => (
												<Box key={key}>{key} → {value as string}</Box>
											))}
										</Box>
									</Box>
								)}
								{selectedAutoPopDef.responseMapping && Object.keys(selectedAutoPopDef.responseMapping).length > 0 && (
									<Box>
										<Box variant="awsui-key-label">Response Mapping</Box>
										<Box>
											{Object.entries(selectedAutoPopDef.responseMapping).map(([key, value]) => (
												<Box key={key}>{key} → {value as string}</Box>
											))}
										</Box>
									</Box>
								)}
							</SpaceBetween>
						)}
					</Container>
				</ContentLayout>
			)}
			</Grid>
		</Container>
	);
}
